import { useCallback, useEffect, useState } from "react";
import { api, fmtError } from "../api";
import { RefreshCw, ShieldAlert, Loader2 } from "lucide-react";

const fmt = (iso) => {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch { return iso || ""; }
};

const KIND_LABEL = {
  ticket_action: "Ticket-Aktion",
  rueckfrage_email: "Rückfrage-E-Mail",
  rueckfrage_push: "Rückfrage-Push",
  rueckfrage_error: "Rückfrage-Fehler",
  inbound_email: "Inbound-E-Mail",
};

const cell = { textAlign: "left", padding: "6px 10px", verticalAlign: "top" };

function Section({ title, testid, rows, type }) {
  const list = rows || [];
  return (
    <div className="card-wd p-0 mb-5 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-3 font-extrabold text-sm" style={{ background: "#F1F5F9", color: "#475569" }}>
        {title} ({list.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", color: "#64748B" }}>
              <th style={cell}>Zeit</th>
              {type === "activity" ? (
                <><th style={cell}>Typ</th><th style={cell}>Von</th><th style={cell}>PermID</th><th style={cell}>Detail</th><th style={cell}>OK</th></>
              ) : (
                <><th style={cell}>Methode</th><th style={cell}>Pfad</th><th style={cell}>Status</th><th style={cell}>ms</th></>
              )}
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.id || i} style={{ borderTop: "1px solid #E2E8F0" }} data-testid={`${type}-row-${i}`}>
                <td style={cell}>{fmt(r.ts)}</td>
                {type === "activity" ? (
                  <>
                    <td style={cell}>{KIND_LABEL[r.kind] || r.kind}</td>
                    <td style={cell}>{r.actor}</td>
                    <td style={{ ...cell, fontFamily: "monospace" }}>{r.permid}</td>
                    <td style={cell}>{r.detail}</td>
                    <td style={{ ...cell, color: r.ok ? "#15803D" : "#DC2626", fontWeight: 700 }}>{r.ok ? "OK" : "FEHLER"}</td>
                  </>
                ) : (
                  <>
                    <td style={cell}>{r.method}</td>
                    <td style={{ ...cell, fontFamily: "monospace", maxWidth: 420, wordBreak: "break-all" }}>{r.path}</td>
                    <td style={{ ...cell, color: r.status >= 400 ? "#DC2626" : "#15803D", fontWeight: 700 }}>{r.status}</td>
                    <td style={cell}>{r.duration_ms}</td>
                  </>
                )}
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={6} style={{ ...cell, color: "#94A3B8" }}>Keine Einträge.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InboundSection({ rows }) {
  const list = rows || [];
  return (
    <div className="card-wd p-0 mb-5 overflow-hidden" data-testid="debug-inbound">
      <div className="px-4 py-3 font-extrabold text-sm" style={{ background: "#F5F3FF", color: "#6D28D9" }}>
        Eingehende E-Mails (Inbound Parse) ({list.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F8FAFC", color: "#64748B" }}>
              <th style={cell}>Zeit</th><th style={cell}>Von</th><th style={cell}>Betreff</th>
              <th style={cell}>Referenz</th><th style={cell}>Zugeordnet</th><th style={cell}>Gepostet</th><th style={cell}>Detail</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.id || i} style={{ borderTop: "1px solid #E2E8F0" }} data-testid={`inbound-row-${i}`}>
                <td style={cell}>{fmt(r.ts)}</td>
                <td style={cell}>{r.from || "—"}</td>
                <td style={{ ...cell, maxWidth: 240, wordBreak: "break-word" }}>{r.subject || "—"}</td>
                <td style={{ ...cell, fontFamily: "monospace" }}>{r.ref || "—"}{r.ref_source ? ` (${r.ref_source})` : ""}</td>
                <td style={{ ...cell, color: r.matched ? "#15803D" : "#DC2626", fontWeight: 700 }}>{r.matched ? "JA" : "NEIN"}</td>
                <td style={{ ...cell, color: r.posted ? "#15803D" : "#94A3B8", fontWeight: 700 }}>{r.posted ? "JA" : "—"}</td>
                <td style={cell}>{r.detail}</td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={7} style={{ ...cell, color: "#94A3B8" }}>Keine Einträge.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DebugLog() {
  const token = sessionStorage.getItem("mag_token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) { setError("Bitte zuerst anmelden."); setStatus(401); return; }
    setLoading(true); setError(""); setStatus(0);
    try {
      const { data } = await api.get("/debug/log", { headers: { Authorization: `Bearer ${token}` }, params: { limit: 200 } });
      setData(data);
    } catch (e) {
      setStatus(e?.response?.status || 0);
      setError(fmtError(e, "Log konnte nicht geladen werden"));
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const forbidden = status === 403;

  return (
    <div className="container-wd py-6" data-testid="debug-log-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-extrabold m-0">Debug-Log</h1>
        <button className="btn-secondary" data-testid="debug-log-refresh" onClick={load} disabled={loading} style={{ padding: "9px 12px" }}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Aktualisieren
        </button>
      </div>

      {(error || forbidden) && (
        <div className="card-wd p-5" data-testid="debug-log-error" style={{ borderColor: forbidden ? "#F59E0B" : "#DC2626" }}>
          <p className="text-sm font-semibold m-0 inline-flex items-center gap-2" style={{ color: forbidden ? "#B45309" : "#DC2626" }}>
            <ShieldAlert size={16} /> {forbidden ? "Kein Zugriff – Administratorrechte erforderlich." : error}
          </p>
        </div>
      )}

      {data && !error && (
        <>
          <Section title="App-Aktivitäten" testid="debug-activity" rows={data.activity} type="activity" />
          <InboundSection rows={data.inbound} />
          <Section title="EDP/MWS-Aufrufe" testid="debug-mws" rows={data.mws} type="mws" />
        </>
      )}
    </div>
  );
}
